import React, {PureComponent} from "react"
import PropTypes from "prop-types"
import {isEqual} from "lodash"
import scrollIntoView from "scroll-into-view-if-needed"

import {FormattedMessage} from "@irati/utils/lang"
import {Hr} from "@irati/ui/src/Layout"
import Loading from "@irati/ui/src/Loading"
import connect from "@irati/store/connect"
import {getQuery} from "@irati/utils/history"
import {geolocationTracking} from "@irati-tracking/geolocation"
import id from "@irati/utils/id"
import {Body} from "@irati/ui/src/Typography"

import addQueryUtil from "../../utils/addQuery"
import ClusterMarker from "./ClusterMarker"
import LocationMarker from "./LocationMarker"
import Cards from "./Cards"
import {prepareMapCardsAndLocations} from "../../helpers/search/location"
import LocationAccuracyInfo from "./LocationAccuracyInfo"
import {
  Container,
  CardsMapContainer,
  CardsContainer,
  MapContainer,
  StyledMap,
  InfoWrapper
} from "./styled/Locations"

class Locations extends PureComponent {
  static propTypes = {
    isPhone: PropTypes.bool.isRequired,
    deals: PropTypes.array.isRequired,
    boundingBox: PropTypes.array.isRequired,
    load: PropTypes.object,
    searchTerm: PropTypes.string,
    mapCenterLatLng: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired
    }),
    userLatLng: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired
    }),
    onMapDataChange: PropTypes.func,
    onMapCenterChange: PropTypes.func
  }

  static defaultProps = {
    load: null,
    searchTerm: null,
    mapCenterLatLng: null,
    userLatLng: null,
    onMapDataChange: () => {},
    onMapCenterChange: null
  }

  state = {
    zoom: parseInt(getQuery().zoom, 10) || null,
    cardsData: [],
    mapData: [],
    activateLocationUuid: null,
    mapClusterZoomHash: {}
  }

  componentDidMount = () => {
    this.init()
  }

  init = () => {
    this.idNamespace = id("locations", this.props)
    this.cardsContainerId = id("cards-container", this.idNamespace)
  }

  componentDidUpdate(prevProps) {
    /** Disabling lint because it is nested in a conditional */
    /* eslint-disable react/no-did-update-set-state */
    const {userLatLng, mapCenterLatLng, deals} = this.props
    if (!isEqual(deals, prevProps.deals)) {
      const {activateLocationUuid} = this.state
      const {cards, locations} = prepareMapCardsAndLocations(deals, userLatLng, mapCenterLatLng)
      this.setState({
        cardsData: cards,
        mapData: locations,
        activateLocationUuid: null
      }, () => {
        // If we have come from a map cluster zoom. activate the location
        if (activateLocationUuid) {
          this.updateDealLocationSelection(activateLocationUuid)
        }
      })
    }
    /* eslint-enable react/no-did-update-set-state */
  }

  handleMapDataChange = (centerLatLng, mapBounds, zoom = null) => {
    this.props.onMapDataChange(centerLatLng, mapBounds)
    if (zoom) {
      addQueryUtil.addQuery({zoom})
      this.setState({zoom})
    }
  }

  updateDealLocationSelection = (selectedDealLocationUuid = null, options = {showDetails: true}) => {
    const {showDetails} = options
    const mapData = this.state.mapData.map((dealLocation) => {
      const dealLocationCopy = Object.assign({}, dealLocation)
      const isMatch = dealLocationCopy.deal.location.uuid === selectedDealLocationUuid
      dealLocationCopy.isActive = isMatch
      dealLocationCopy.showDetails = isMatch && showDetails
      dealLocationCopy.zIndex = isMatch ? 2 : 1
      return dealLocationCopy
    })

    this.setState({
      mapData
    })
  }

  handleMapClick = () => {
    // clear any active deal locations if the map was clicked (but not a marker)
    this.updateDealLocationSelection()
  }

  handleCardSelection = (selectedDealLocation) => {
    const {uuid, lat, lng} = selectedDealLocation
    // If location is part of a cluster, zoom in
    const goToZoom = this.state.mapClusterZoomHash[uuid]
    if (goToZoom) {
      this.props.onMapCenterChange({lat, lng})
      this.setState({
        zoom: goToZoom,
        activateLocationUuid: uuid
      })
    } else {
      this.updateDealLocationSelection(uuid)
    }
    geolocationTracking.searchByLocationClickAction("list", this.props.searchTerm)
  }

  handleMapClusterZoomHashTableData = (data) => {
    if (!isEqual(data, this.state.mapClusterZoomHash)) {
      this.setState({
        mapClusterZoomHash: data
      })
    }
  }

  handleLocationMarkerClick = (selectedDealLocationUuid, index) => {
    this.updateDealLocationSelection(selectedDealLocationUuid)
    const {searchTerm, isPhone} = this.props
    if (isPhone) {
      this.cards.goToSliderIndex(index)
      scrollIntoView(document.getElementById(this.cardsContainerId), {
        scrollMode: "if-needed",
        behavior: "smooth"
      })
    }
    geolocationTracking.searchByLocationClickAction("map", searchTerm)
  }

  render() {
    const {mapCenterLatLng, userLatLng, isPhone, boundingBox, deals, searchTerm, load} = this.props
    const {mapData, cardsData, zoom} = this.state
    const settings = {}
    if (zoom) {settings.zoom = zoom}

    return (
      <Container>
        {mapCenterLatLng ?
          <>
            <Hr />
            <InfoWrapper>
              <Body small marginBottom={0}>
                <FormattedMessage
                  id="search.containers.searchAllByLocation.resultsCount"
                  values={{
                    dealsCount: cardsData.length,
                    locationsCount: deals.length
                  }} />
              </Body>
              <LocationAccuracyInfo />
            </InfoWrapper>
            <CardsMapContainer>
              <CardsContainer id={this.cardsContainerId}>
                <Loading load={load}>
                  <Cards
                    ref={(c) => {if (c) {this.cards = c.getWrappedInstance()} }}
                    data={cardsData}
                    onCardSelection={this.handleCardSelection}
                    searchTerm={searchTerm} />
                </Loading>
              </CardsContainer>
              <MapContainer>
                <StyledMap
                  centerLatLng={mapCenterLatLng}
                  userLatLng={userLatLng}
                  data={mapData}
                  onDataChanged={this.handleMapDataChange}
                  onClusterZoomHashTableData={this.handleMapClusterZoomHashTableData}
                  onClick={this.handleMapClick}
                  LocationMarker={(props) => (
                    <LocationMarker
                      {...props}
                      boundingBox={boundingBox}
                      searchTerm={searchTerm}
                      onClick={this.handleLocationMarkerClick} />
                  )}
                  ClusterMarker={ClusterMarker}
                  googleMapsOptions={{
                    clickableIcons: false,
                    zoomControl: !isPhone,
                    fullscreenControl: false,
                    minZoom: 12
                  }}
                  settings={settings} />
              </MapContainer>
            </CardsMapContainer>
          </>
          : <FormattedMessage id="search.containers.searchAllByLocation.noLocation" />}
      </Container>
    )
  }
}

const mapStateToProps = ({mq}) => ({isPhone: mq.lessThan.large})

export default connect(mapStateToProps)(Locations)
